import styled from "styled-components";
import { IVennCircle, IVennCircleWrapper } from "../../../Models/Models";
import { device } from "../../../globalStyles/breakpoints";

const percentLineWidth = 12;
const percentLineTotalWidth = 2;
const percentLineShaveOffPercent = 0.25;
const lineIndicatorWidth = 40;
const valueTextOffset = lineIndicatorWidth;
const transitionTime = 0.5;
const transitionDelay = 0.3;
const lineIndicatorHeight = 1;
const percentTextFontSize = 21;
const centralPercentTextFontSizeMobile = 21;
const centralPercentTextFontSizeDesktop = 28;
const locationNameLRPosition = 20;
const locationNameMobileFontSize = 17;
const locationNameDesktopFontSize = 21;

//  ██████╗ █████╗ ██╗      ██████╗███████╗
// ██╔════╝██╔══██╗██║     ██╔════╝██╔════╝
// ██║     ███████║██║     ██║     ███████╗
// ██║     ██╔══██║██║     ██║     ╚════██║
// ╚██████╗██║  ██║███████╗╚██████╗███████║
//  ╚═════╝╚═╝  ╚═╝╚══════╝ ╚═════╝╚══════╝

const calcCircleRadius = (circleDiam: number): number => {
    return circleDiam / 2;
};

const calcCircleOffsetIncrease = (
    increasedCircleWidth: number,
    borderWidth: number,
    circleDiam: number
): number => {
    const increasedOffset = (increasedCircleWidth - circleDiam) / 2 + borderWidth;
    return calcCircleOffset(circleDiam) - increasedOffset;
};

const calcCircleRadiusIncrease = (
    increasedCircleWidth: number,
    circleDiam: number
): number => {
    return (increasedCircleWidth - circleDiam) / 2;
};

const calcCircleWidth = (circleDiam: number, borderWidth: number): number => {
    return circleDiam + 2 * borderWidth;
};

const calcLineIndicatorUpperBound = (circDiam: number) => {
    return (
        circDiam
    + 2 * lineIndicatorWidth
    - calcClipPathTop(circDiam, lineIndicatorWidth)
    );
};

const topLineIndicatorClipPath = (
    percent: number,
    circDiam: number
): number => {
    const lineIndicatorUpB = calcLineIndicatorUpperBound(circDiam);
    const totalLineIndDiff
    = lineIndicatorUpB
    - calcClipPathTop(circDiam, lineIndicatorWidth - percentLineTotalWidth);
    const lineIndicatorPercent = percent * totalLineIndDiff;
    return lineIndicatorUpB - lineIndicatorPercent;
};

const bottomLineIndicatorClipPath = (
    percent: number,
    circleDiam: number
): number => {
    const lineIndicatorUpB = calcLineIndicatorUpperBound(circleDiam);
    const totalLineIndDiff
    = lineIndicatorUpB
    - calcClipPathTop(circleDiam, lineIndicatorWidth - percentLineTotalWidth);
    const lineIndicatorPercent = percent * totalLineIndDiff;
    return (
        calcClipPathTop(circleDiam, lineIndicatorWidth)
    + lineIndicatorPercent
    - lineIndicatorHeight
    );
};

const topPercentLine = (percent: number, circleDiam: number): number => {
    const totalPercentLineDiff
    = calcPercentLineZero(circleDiam) - calcPercentLine100(circleDiam);
    const percentTopLinePercent = percent * totalPercentLineDiff;
    return (
        calcPercentLineZero(circleDiam)
    - percentTopLinePercent
    + (1 - percent) * percentLineWidth
    - percent * percentLineTotalWidth
    ); // <-- this last little calculation is a bit arbitrary, but there's something missing in the space between the line indicator and percent line in higher percentages.
};

const calcShaveOff = (circleDiam: number): number => {
    return percentLineShaveOffPercent * calcCircleRadius(circleDiam);
};

const calcPercentText100 = (circleDiam: number): number => {
    const textOffset = percentTextFontSize / 2;
    return calcShaveOff(circleDiam) - textOffset;
};

const calcPercentTextZero = (circleDiam: number): number => {
    const textOffset = percentTextFontSize / 2;
    return circleDiam - calcShaveOff(circleDiam) - textOffset;
};

const percentTextPadding = (percent: number, circleDiam: number): number => {
    const totalPercentTextDiff
    = calcPercentTextZero(circleDiam) - calcPercentText100(circleDiam);
    const percentTextPercent = (1 - percent) * totalPercentTextDiff;
    return calcPercentText100(circleDiam) + percentTextPercent;
};

const calcClipPathTop = (circleDiam: number, lineWidth: number): number => {
    return lineWidth + calcShaveOff(circleDiam); // Shave off 20% of the circle's radius and add the percentLine width.
};

const calcPercentLineZero = (circleDiam: number): number => {
    return circleDiam - calcShaveOff(circleDiam);
};

const calcPercentLine100 = (circleDiam: number): number => {
    return calcShaveOff(circleDiam) + percentLineWidth;
};

const calcPercentLineTotalClipPath = (circleDiam: number): number => {
    const diffPercentAndPercentTotalWidth
    = percentLineWidth - percentLineTotalWidth;
    return (
        calcClipPathTop(circleDiam, percentLineWidth)
    - diffPercentAndPercentTotalWidth
    );
};

const calcRadiusStartPointForCounterCircle = (
    circleDiam: number,
    side: "left" | "right"
): number => {
    let startPoint = (circleDiam - calcCircleOffset(circleDiam)) / 2; // so, the offset is 2/3, I want the leftover (1/3) then divided by 2 because one will be negative, the other positive, making up the 2 halves.
    if (side === "left") {
        startPoint += circleDiam;
    } else {
        startPoint = -startPoint;
    }
    return startPoint;
};

const calcCircleOffset = (circleDiam: number): number => {
    return (2 * circleDiam) / 3; // offset, currently, is 2/3rds.
};

// ███████╗ ██████╗███████╗███████╗
// ██╔════╝██╔════╝██╔════╝██╔════╝
// ███████╗██║     ███████╗███████╗
// ╚════██║██║     ╚════██║╚════██║
// ███████║╚██████╗███████║███████║
// ╚══════╝ ╚═════╝╚══════╝╚══════╝

export const PercentText = styled.div`
  opacity: 0;
  font-size: ${percentTextFontSize}px;
  transition: opacity ${transitionTime}s;
`;

export const CentralPercent = styled.div`
  font-size: ${centralPercentTextFontSizeMobile}px;
  font-weight: 800;
  opacity: 0;
  transition: opacity ${transitionTime}s, top ${transitionTime}s;
  transition-delay: ${transitionDelay}s;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 6;
  margin: 0 auto;
  text-align: center;
  transform: translate(
    0,
    calc(50% - ${centralPercentTextFontSizeMobile / 2}px)
  );
  ${device.tabletL} {
    font-size: ${centralPercentTextFontSizeDesktop}px;
    transform: translate(
      0,
      calc(50% - ${centralPercentTextFontSizeDesktop / 2}px)
    );
  }
`;

export const CircleInnerWrap = styled.div`
  position: relative;
  margin: 0 auto;
  > svg {
    color: black;
    position: absolute;
    bottom: 0;
    transition: opacity ${transitionTime}s, top ${transitionTime}s;
    opacity: 0;
    transition-delay: ${transitionDelay}s;
  }
  &.active {
    > svg {
      opacity: 1;
    }
    ${CentralPercent} {
      top: 0;
      opacity: 1;
    }
  }
`;

export const CircleOuterWrap = styled.div`
  height: ${(props: IVennCircleWrapper) => props.mobileInitDiam}px;
  width: 100%;
  color: white;
  overflow: hidden;
  ${CircleInnerWrap} {
    height: ${(props: IVennCircleWrapper) => props.mobileInitDiam}px;
    width: ${(props: IVennCircleWrapper) =>
        calcCircleOffset(props.mobileInitDiam) + props.mobileInitDiam}px;
    > svg {
      width: ${(props: IVennCircleWrapper) => props.mobileInitDiam / 2}px;
      height: ${(props: IVennCircleWrapper) => props.mobileInitDiam / 2}px;
      top: ${(props: IVennCircleWrapper) => props.mobileInitDiam / 10}px;
      left: ${(props: IVennCircleWrapper) =>
        props.mobileInitDiam / 12 + props.mobileInitDiam / 2}px;
    }
    &.active {
      > svg {
        top: ${(props: IVennCircleWrapper) => props.mobileInitDiam / 4}px;
      }
    }
  }
  ${CentralPercent} {
    top: ${(props: IVennCircleWrapper) => props.mobileInitDiam / 2}px;
  }
  ${device.tabletL} {
    height: ${(props: IVennCircleWrapper) => props.desktopInitDiam}px;
    ${CircleInnerWrap} {
      height: ${(props: IVennCircleWrapper) => props.desktopInitDiam}px;
      width: ${(props: IVennCircleWrapper) =>
        calcCircleOffset(props.desktopInitDiam) + props.desktopInitDiam}px;
      > svg {
        width: ${(props: IVennCircleWrapper) => props.desktopInitDiam / 2}px;
        height: ${(props: IVennCircleWrapper) => props.desktopInitDiam / 2}px;
        top: ${(props: IVennCircleWrapper) => props.desktopInitDiam / 10}px;
        left: ${(props: IVennCircleWrapper) =>
        props.desktopInitDiam / 12 + props.desktopInitDiam / 2}px;
      }
      &.active {
        > svg {
          top: ${(props: IVennCircleWrapper) => props.desktopInitDiam / 4}px;
        }
      }
    }
    ${CentralPercent} {
      top: ${(props: IVennCircleWrapper) => props.desktopInitDiam / 2}px;
    }
  }
`;

export const PercentLine = styled.div`
  transition: all ${transitionTime}s;
  border: ${percentLineWidth}px solid white;
  border-radius: 50%;
  position: absolute;
  opacity: 0;
`;

export const PercentLineTotal = styled.div`
  transition: all ${transitionTime}s;
  border: ${percentLineTotalWidth}px solid white;
  border-radius: 50%;
  position: absolute;
  opacity: 0;
`;

export const LineIndicator = styled.div`
  transition: all ${transitionTime}s;
  border: ${lineIndicatorWidth}px solid white;
  border-radius: 50%;
  position: absolute;
  opacity: 0;
`;

export const PercentCounterCircle = styled.div`
  transition: all ${transitionTime}s;
  opacity: 0.5;
`;

export const LocationName = styled.div`
  font-size: ${locationNameMobileFontSize}px;
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  text-align: center;
  opacity: 0;
  transition: opacity ${transitionTime}s, left ${transitionTime}s,
    right ${transitionTime}s;
  font-weight: 800;
  > div {
    font-weight: 400;
  }
  ${device.tabletL} {
    font-size: ${locationNameDesktopFontSize}px;
  }
`;

const Circle = styled.div`
  /* ${(props: IVennCircle) =>
        props.percent} */ /* ERY: don't ask. some weird bug in styled components */
  shape-outside: circle();
  clip-path: circle();
  width: ${(props: IVennCircle) => props.mobileInitDiam}px;
  height: ${(props: IVennCircle) => props.mobileInitDiam}px;
  position: absolute;
  opacity: 0.8;
  overflow: hidden;
  background-color: ${(props: IVennCircle) => props.backgroundColor};
  ${PercentText} {
    /* Powered by Data */
    padding-top: ${(props: IVennCircle) =>
        props.percent
            ? percentTextPadding(props.percent, props.mobileInitDiam)
            : calcPercentTextZero(props.mobileInitDiam)}px;
  }
  ${PercentLine} {
    width: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileInitDiam, percentLineWidth)}px;
    height: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileInitDiam, percentLineWidth)}px;

    /* Powered by data */
    clip-path: inset(
      ${(props: IVennCircle) =>
        props.percent
            ? topPercentLine(props.percent, props.mobileInitDiam)
            : calcPercentLineZero(props.mobileInitDiam)}px
        0px
        ${(props: IVennCircle) => calcPercentLine100(props.mobileInitDiam)}px
    );
    /* 0% indication is inset(405px 0px 68px) at full width */
    /* 100% indication is inset(68px 0 68px) at full width */
  }
  ${PercentLineTotal} {
    width: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileInitDiam, percentLineTotalWidth)}px;
    height: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileInitDiam, percentLineTotalWidth)}px;
    clip-path: inset(
      ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.mobileInitDiam)}px
        0px
        ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.mobileInitDiam)}px
    );
    /* ERY: This does not in/decrease linearly with the 
    .percent-line (they were at inset(80px 0 80px) each, 
    but now they're off by 13px. I'll bet is has to do 
    with circumference and probably changes at a rate 
    of pi )*/
  }
  ${LineIndicator} {
    width: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileInitDiam, lineIndicatorWidth)}px;
    height: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileInitDiam, lineIndicatorWidth)}px;

    /* Powered by data */
    clip-path: inset(
      ${(props: IVennCircle) =>
        props.percent
            ? topLineIndicatorClipPath(props.percent, props.mobileInitDiam)
            : calcLineIndicatorUpperBound(props.mobileInitDiam)}px
        0
        ${(props: IVennCircle) =>
        props.percent
            ? bottomLineIndicatorClipPath(props.percent, props.mobileInitDiam)
            : calcClipPathTop(props.mobileInitDiam, lineIndicatorWidth)}px
    );
    /* 0% indication is inset(433px 0 96px) at full width */
    /* 100% indication is inset(96px 0 433px) at full width */
  }
  ${PercentCounterCircle} {
    width: ${(props: IVennCircle) => props.mobileInitDiam}px;
    height: ${(props: IVennCircle) => props.mobileInitDiam}px;
  }
  ${LocationName} {
    width: ${(props: IVennCircle) => (2 * props.mobileInitDiam) / 6}px;
  }
  &.active {
    ${PercentLine} {
      width: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileExpDiam, percentLineWidth)}px;
      height: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileExpDiam, percentLineWidth)}px;
      top: ${(props: IVennCircle) =>
        (props.mobileInitDiam - props.mobileExpDiam) / 2 - percentLineWidth}px;
      clip-path: inset(
        ${(props: IVennCircle) =>
        props.percent
            ? topPercentLine(props.percent, props.mobileInitDiam)
                + calcCircleRadiusIncrease(
                    props.mobileExpDiam,
                    props.mobileInitDiam
                )
            : calcPercentLineZero(props.mobileInitDiam)
                + calcCircleRadiusIncrease(
                    props.mobileExpDiam,
                    props.mobileInitDiam
                )}px
          0px
          ${(props: IVennCircle) =>
        calcPercentLine100(props.mobileInitDiam)
            + calcCircleRadiusIncrease(
                props.mobileExpDiam,
                props.mobileInitDiam
            )}px
      );
    }
    ${PercentLineTotal} {
      width: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileExpDiam, percentLineTotalWidth)}px;
      height: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileExpDiam, percentLineTotalWidth)}px;
      top: ${(props: IVennCircle) =>
        (props.mobileInitDiam - props.mobileExpDiam) / 2
        - percentLineTotalWidth}px;
      clip-path: inset(
        ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.mobileInitDiam)
            + calcCircleRadiusIncrease(
                props.mobileExpDiam,
                props.mobileInitDiam
            )}px
          0px
          ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.mobileInitDiam)
            + calcCircleRadiusIncrease(
                props.mobileExpDiam,
                props.mobileInitDiam
            )}px
      );
    }
    ${LineIndicator} {
      width: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileExpDiam, lineIndicatorWidth)}px;
      height: ${(props: IVennCircle) =>
        calcCircleWidth(props.mobileExpDiam, lineIndicatorWidth)}px;
      top: ${(props: IVennCircle) =>
        (props.mobileInitDiam - props.mobileExpDiam) / 2
        - lineIndicatorWidth}px;
      clip-path: inset(
        ${(props: IVennCircle) =>
        props.percent
            ? topLineIndicatorClipPath(props.percent, props.mobileInitDiam)
                + calcCircleRadiusIncrease(
                    props.mobileExpDiam,
                    props.mobileInitDiam
                )
            : calcLineIndicatorUpperBound(props.mobileInitDiam)
                + calcCircleRadiusIncrease(
                    props.mobileExpDiam,
                    props.mobileInitDiam
                )}px
          0
          ${(props: IVennCircle) =>
        props.percent
            ? bottomLineIndicatorClipPath(
                props.percent,
                props.mobileInitDiam
            )
                + calcCircleRadiusIncrease(
                    props.mobileExpDiam,
                    props.mobileInitDiam
                )
            : calcClipPathTop(props.mobileInitDiam, lineIndicatorWidth)
                + calcCircleRadiusIncrease(
                    props.mobileExpDiam,
                    props.mobileInitDiam
                )}px
      );
    }
    ${PercentLine}, ${PercentLineTotal}, ${LineIndicator}, ${PercentCounterCircle}, ${PercentText}, ${LocationName} {
      opacity: 1;
    }
  }
  ${device.tabletL} {
    width: ${(props: IVennCircle) => props.desktopInitDiam}px;
    height: ${(props: IVennCircle) => props.desktopInitDiam}px;
    ${PercentText} {
      padding-top: ${(props: IVennCircle) =>
        props.percent
            ? percentTextPadding(props.percent, props.desktopInitDiam)
            : calcPercentTextZero(props.desktopInitDiam)}px;
    }
    ${PercentLine} {
      width: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopInitDiam, percentLineWidth)}px;
      height: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopInitDiam, percentLineWidth)}px;

      /* Powered by data */
      clip-path: inset(
        ${(props: IVennCircle) =>
        props.percent
            ? topPercentLine(props.percent, props.desktopInitDiam)
            : calcPercentLineZero(props.desktopInitDiam)}px
          0px
          ${(props: IVennCircle) => calcPercentLine100(props.desktopInitDiam)}px
      );
    }
    ${PercentLineTotal} {
      width: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopInitDiam, percentLineTotalWidth)}px;
      height: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopInitDiam, percentLineTotalWidth)}px;
      clip-path: inset(
        ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.desktopInitDiam)}px
          0px
          ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.desktopInitDiam)}px
      );
    }
    ${LineIndicator} {
      width: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopInitDiam, lineIndicatorWidth)}px;
      height: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopInitDiam, lineIndicatorWidth)}px;

      /* Powered by data */
      clip-path: inset(
        ${(props: IVennCircle) =>
        props.percent
            ? topLineIndicatorClipPath(props.percent, props.desktopInitDiam)
            : calcLineIndicatorUpperBound(props.desktopInitDiam)}px
          0
          ${(props: IVennCircle) =>
        props.percent
            ? bottomLineIndicatorClipPath(
                props.percent,
                props.desktopInitDiam
            )
            : calcClipPathTop(props.desktopInitDiam, lineIndicatorWidth)}px
      );
    }
    ${PercentCounterCircle} {
      width: ${(props: IVennCircle) => props.desktopInitDiam}px;
      height: ${(props: IVennCircle) => props.desktopInitDiam}px;
    }
    ${LocationName} {
      width: ${(props: IVennCircle) => (2 * props.desktopInitDiam) / 6}px;
    }
    &.active {
      ${PercentLine} {
        width: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopExpDiam, percentLineWidth)}px;
        height: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopExpDiam, percentLineWidth)}px;
        top: ${(props: IVennCircle) =>
        (props.desktopInitDiam - props.desktopExpDiam) / 2
          - percentLineWidth}px;
        clip-path: inset(
          ${(props: IVennCircle) =>
        props.percent
            ? topPercentLine(props.percent, props.desktopInitDiam)
                  + calcCircleRadiusIncrease(
                      props.desktopExpDiam,
                      props.desktopInitDiam
                  )
            : calcPercentLineZero(props.desktopInitDiam)
                  + calcCircleRadiusIncrease(
                      props.desktopExpDiam,
                      props.desktopInitDiam
                  )}px
            0px
            ${(props: IVennCircle) =>
        calcPercentLine100(props.desktopInitDiam)
              + calcCircleRadiusIncrease(
                  props.desktopExpDiam,
                  props.desktopInitDiam
              )}px
        );
      }
      ${PercentLineTotal} {
        width: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopExpDiam, percentLineTotalWidth)}px;
        height: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopExpDiam, percentLineTotalWidth)}px;
        top: ${(props: IVennCircle) =>
        (props.desktopInitDiam - props.desktopExpDiam) / 2
          - percentLineTotalWidth}px;
        clip-path: inset(
          ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.desktopInitDiam)
              + calcCircleRadiusIncrease(
                  props.desktopExpDiam,
                  props.desktopInitDiam
              )}px
            0px
            ${(props: IVennCircle) =>
        calcPercentLineTotalClipPath(props.desktopInitDiam)
              + calcCircleRadiusIncrease(
                  props.desktopExpDiam,
                  props.desktopInitDiam
              )}px
        );
      }
      ${LineIndicator} {
        width: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopExpDiam, lineIndicatorWidth)}px;
        height: ${(props: IVennCircle) =>
        calcCircleWidth(props.desktopExpDiam, lineIndicatorWidth)}px;
        top: ${(props: IVennCircle) =>
        (props.desktopInitDiam - props.desktopExpDiam) / 2
          - lineIndicatorWidth}px;
        clip-path: inset(
          ${(props: IVennCircle) =>
        props.percent
            ? topLineIndicatorClipPath(
                props.percent,
                props.desktopInitDiam
            )
                  + calcCircleRadiusIncrease(
                      props.desktopExpDiam,
                      props.desktopInitDiam
                  )
            : calcLineIndicatorUpperBound(props.desktopInitDiam)
                  + calcCircleRadiusIncrease(
                      props.desktopExpDiam,
                      props.desktopInitDiam
                  )}px
            0
            ${(props: IVennCircle) =>
        props.percent
            ? bottomLineIndicatorClipPath(
                props.percent,
                props.desktopInitDiam
            )
                  + calcCircleRadiusIncrease(
                      props.desktopExpDiam,
                      props.desktopInitDiam
                  )
            : calcClipPathTop(props.desktopInitDiam, lineIndicatorWidth)
                  + calcCircleRadiusIncrease(
                      props.desktopExpDiam,
                      props.desktopInitDiam
                  )}px
        );
      }
    }
  }
`;

export const CircleLeft = styled(Circle)`
  /* ${(props: IVennCircle) =>
        props.percent} */ /* ERY: don't ask. some weird bug in styled components */
  ${PercentLine} {
    left: ${(props: IVennCircle) =>
        calcCircleOffset(props.mobileInitDiam) - percentLineWidth}px;
    top: -${percentLineWidth}px;
  }
  ${PercentLineTotal} {
    left: ${(props: IVennCircle) =>
        calcCircleOffset(props.mobileInitDiam) - percentLineTotalWidth}px;
    top: -${percentLineTotalWidth}px;
  }
  ${LineIndicator} {
    left: ${(props: IVennCircle) =>
        calcCircleOffset(props.mobileInitDiam) - lineIndicatorWidth}px;
    top: -${lineIndicatorWidth}px;
  }
  ${PercentCounterCircle} {
    shape-outside: circle(
      ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileInitDiam) + valueTextOffset}px
        at
        ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(props.mobileInitDiam, "left")}px
    ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
    clip-path: circle(
      ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileInitDiam) + valueTextOffset}px
        at
        ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(props.mobileInitDiam, "left")}px
    );
    float: right;
  }
  ${PercentText} {
    text-align: right;
  }
  ${LocationName} {
    left: 0px;
    text-align: left;
  }
  &.active {
    ${PercentLine} {
      left: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.mobileExpDiam,
            percentLineWidth,
            props.mobileInitDiam
        )}px;
    }
    ${PercentLineTotal} {
      left: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.mobileExpDiam,
            percentLineTotalWidth,
            props.mobileInitDiam
        )}px;
    }
    ${LineIndicator} {
      left: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.mobileExpDiam,
            lineIndicatorWidth,
            props.mobileInitDiam
        )}px;
    }

    ${PercentCounterCircle} {
      shape-outside: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileExpDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.mobileInitDiam,
            "left"
        )}px
      ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
      clip-path: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileExpDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.mobileInitDiam,
            "left"
        )}px
      );
    }
    ${LocationName} {
      left: ${locationNameLRPosition}px;
    }
  }

  ${device.tabletL} {
    ${PercentLine} {
      left: ${(props: IVennCircle) =>
        calcCircleOffset(props.desktopInitDiam) - percentLineWidth}px;
    }
    ${PercentLineTotal} {
      left: ${(props: IVennCircle) =>
        calcCircleOffset(props.desktopInitDiam) - percentLineTotalWidth}px;
    }
    ${LineIndicator} {
      left: ${(props: IVennCircle) =>
        calcCircleOffset(props.desktopInitDiam) - lineIndicatorWidth}px;
    }
    ${PercentCounterCircle} {
      shape-outside: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopInitDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "left"
        )}px
      ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
      clip-path: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopInitDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "left"
        )}px
      );
    }
    &.active {
      ${PercentLine} {
        left: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.desktopExpDiam,
            percentLineWidth,
            props.desktopInitDiam
        )}px;
      }
      ${PercentLineTotal} {
        left: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.desktopExpDiam,
            percentLineTotalWidth,
            props.desktopInitDiam
        )}px;
      }
      ${LineIndicator} {
        left: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.desktopExpDiam,
            lineIndicatorWidth,
            props.desktopInitDiam
        )}px;
      }

      ${PercentCounterCircle} {
        shape-outside: circle(
          ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopExpDiam) + valueTextOffset}px
            at
            ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "left"
        )}px
        ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
        clip-path: circle(
          ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopExpDiam) + valueTextOffset}px
            at
            ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "left"
        )}px
        );
      }
    }
  }
`;

export const CircleRight = styled(Circle)`
  /* ${(props: IVennCircle) => props.percent} */
  left: ${(props: IVennCircle) => calcCircleOffset(props.mobileInitDiam)}px;
  ${PercentLine} {
    right: ${(props: IVennCircle) =>
        calcCircleOffset(props.mobileInitDiam) - percentLineWidth}px;
    top: -${percentLineWidth}px;
    transition-delay: ${transitionDelay * 2}s;
  }
  ${PercentLineTotal} {
    right: ${(props: IVennCircle) =>
        calcCircleOffset(props.mobileInitDiam) - percentLineTotalWidth}px;
    top: -${percentLineTotalWidth}px;
    transition-delay: ${transitionDelay * 2}s;
  }
  ${LineIndicator} {
    right: ${(props: IVennCircle) =>
        calcCircleOffset(props.mobileInitDiam) - lineIndicatorWidth}px;
    top: -${lineIndicatorWidth}px;
    transition-delay: ${transitionDelay * 2}s;
  }
  /* ERY: Need to change the offset to the center of the circle to match the center of the circle parent (curve and all that) */
  ${PercentCounterCircle} {
    shape-outside: circle(
      ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileInitDiam) + valueTextOffset}px
        at
        ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(props.mobileInitDiam, "right")}px
    ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
    clip-path: circle(
      ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileInitDiam) + valueTextOffset}px
        at
        ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(props.mobileInitDiam, "right")}px
    );
    float: left;
    transition-delay: ${transitionDelay * 2}s;
  }
  ${PercentText} {
    transition-delay: ${transitionDelay * 2}s;
  }
  ${LocationName} {
    right: 0px;
    text-align: right;
    transition-delay: ${transitionDelay * 2}s;
  }
  &.active {
    ${PercentLine} {
      right: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.mobileExpDiam,
            percentLineWidth,
            props.mobileInitDiam
        )}px;
    }
    ${PercentLineTotal} {
      right: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.mobileExpDiam,
            percentLineTotalWidth,
            props.mobileInitDiam
        )}px;
    }
    ${LineIndicator} {
      right: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.mobileExpDiam,
            lineIndicatorWidth,
            props.mobileInitDiam
        )}px;
    }
    ${PercentCounterCircle} {
      shape-outside: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileExpDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.mobileInitDiam,
            "right"
        )}px
      ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
      clip-path: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.mobileExpDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.mobileInitDiam,
            "right"
        )}px
      );
    }
    ${LocationName} {
      right: ${locationNameLRPosition}px;
    }
  }
  ${device.tabletL} {
    left: ${(props: IVennCircle) => calcCircleOffset(props.desktopInitDiam)}px;
    ${PercentLine} {
      right: ${(props: IVennCircle) =>
        calcCircleOffset(props.desktopInitDiam) - percentLineWidth}px;
      top: -${percentLineWidth}px;
    }
    ${PercentLineTotal} {
      right: ${(props: IVennCircle) =>
        calcCircleOffset(props.desktopInitDiam) - percentLineTotalWidth}px;
      top: -${percentLineTotalWidth}px;
    }
    ${LineIndicator} {
      right: ${(props: IVennCircle) =>
        calcCircleOffset(props.desktopInitDiam) - lineIndicatorWidth}px;
      top: -${lineIndicatorWidth}px;
    }
    /* ERY: Need to change the offset to the center of the circle to match the center of the circle parent (curve and all that) */
    ${PercentCounterCircle} {
      shape-outside: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopInitDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "right"
        )}px
      ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
      clip-path: circle(
        ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopInitDiam) + valueTextOffset}px
          at
          ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "right"
        )}px
      );
    }
    &.active {
      ${PercentLine} {
        right: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.desktopExpDiam,
            percentLineWidth,
            props.desktopInitDiam
        )}px;
      }
      ${PercentLineTotal} {
        right: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.desktopExpDiam,
            percentLineTotalWidth,
            props.desktopInitDiam
        )}px;
      }
      ${LineIndicator} {
        right: ${(props: IVennCircle) =>
        calcCircleOffsetIncrease(
            props.desktopExpDiam,
            lineIndicatorWidth,
            props.desktopInitDiam
        )}px;
      }
      ${PercentCounterCircle} {
        shape-outside: circle(
          ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopExpDiam) + valueTextOffset}px
            at
            ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "right"
        )}px
        ); /* Outer edge of the indicator, which is a total width/height of 560 x 560 */
        clip-path: circle(
          ${(props: IVennCircle) =>
        calcCircleRadius(props.desktopExpDiam) + valueTextOffset}px
            at
            ${(props: IVennCircle) =>
        calcRadiusStartPointForCounterCircle(
            props.desktopInitDiam,
            "right"
        )}px
        );
      }
    }
  }
`;
